import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from './_app';
import React from 'react';
import Layout from '../index-layouts';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getPageContentContentApi, orderSubsQueryRequest } from '@/lib/service';
import PlanPage from '@/components/plan/PlanPage';
import { getTdkApi } from '@/lib/service/tdk';
import { pricingJsonLd, pricingStructure } from '@/lib/common/jsonld';
import { DynamicISRDebugPanel as ISRDebugPanel } from '@/components/ISRDebugPanel/dynamic';
import { ISR_REVALIDATE_TIME } from '@/lib/config/isr';

// ⭐ ISR: 转换为静态生成 + 2 分钟重新验证
// 注意：非动态路由会自动为所有配置的语言生成页面
export const getStaticProps: GetStaticProps = async (context) => {
  const { locale = 'en' } = context;
  const resolvedUrl = '/plan';

  // ⭐ ISR 测试：服务端调试日志
  console.log(`[ISR] 🔄 Regenerating page: /plan | Locale: ${locale} | Time: ${new Date().toLocaleString('zh-CN')}`);

  const localeRes = await serverSideTranslations(locale, ['common']);
  let currentURL = `${process.env.NEXT_APP_WEB_HOST}${resolvedUrl}`;
  if (locale != 'en') {
    currentURL = `${process.env.NEXT_APP_WEB_HOST}/${locale}${resolvedUrl}`;
  }
  const tdkData = await getTdkApi(resolvedUrl, locale);
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};

  const res = await orderSubsQueryRequest(null);
  const siteUrl = locale !== 'en' ? `${process.env.NEXT_APP_WEB_HOST}/${locale}` : `${process.env.NEXT_APP_WEB_HOST}`;

  const pageTitle = tdkData?.data?.title ?? tempData?.head_Meta_plan_Title ?? null;
  const pageDescription = tdkData?.data?.description ?? tempData?.head_Meta_plan_Desc ?? null;
  const planList = res.data ?? [];
  const planItems: pricingStructure[] = planList.map((plan: any) => {
    return {
      name: plan.name,
      price: plan.amount,
      currency: 'USD',
    };
  });

  const resData = await getPageContentContentApi(`/plan`, locale, 0, '');

  const jsonLd = pricingJsonLd(
    {
      title: pageTitle,
      desc: pageDescription,
      url: currentURL,
    },
    { url: siteUrl },
    planItems,
  );
  return {
    props: {
      ...localeRes,
      herf: currentURL,
      locale,
      planList: planList,
      title: pageTitle,
      description: pageDescription,
      jsonLd: jsonLd,
      // ⭐ ISR 信息
      isrGeneratedAt: new Date().toISOString(),
      isrRevalidateIn: ISR_REVALIDATE_TIME,
      data: resData.data,
    },
    revalidate: ISR_REVALIDATE_TIME,
  };
};

const Home: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props: any) => {
  return (
    <>
      <ISRDebugPanel
        generatedAt={props.isrGeneratedAt}
        revalidateIn={props.isrRevalidateIn}
        pageName="定价页"
        additionalInfo={{
          语言: props.locale,
          套餐数量: props.planList?.length || 0,
        }}
      />
      <PlanPage data={props} />
    </>
  );
};

Home.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;
  const jsonLd = page?.props?.jsonLd;
  return (
    <Layout
      head={
        <>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta
            property="og:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta
            property="og:image:secure_url"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta
            name="twitter:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="alternate" hrefLang="en" href={`${process.env.NEXT_APP_WEB_HOST}/en/plan`} />
          <link rel="alternate" hrefLang="zh" href={`${process.env.NEXT_APP_WEB_HOST}/zh/plan`} />
          <link rel="alternate" hrefLang="tw" href={`${process.env.NEXT_APP_WEB_HOST}/tw/plan`} />
          <link rel="alternate" hrefLang="ja" href={`${process.env.NEXT_APP_WEB_HOST}/ja/plan`} />
          <link rel="alternate" hrefLang="ko" href={`${process.env.NEXT_APP_WEB_HOST}/ko/plan`} />
          <link rel="alternate" hrefLang="pt" href={`${process.env.NEXT_APP_WEB_HOST}/pt/plan`} />
          <link rel="alternate" hrefLang="de" href={`${process.env.NEXT_APP_WEB_HOST}/de/plan`} />
          <link rel="alternate" hrefLang="es" href={`${process.env.NEXT_APP_WEB_HOST}/es/plan`} />
          <link rel="alternate" hrefLang="it" href={`${process.env.NEXT_APP_WEB_HOST}/it/plan`} />
          <link rel="alternate" hrefLang="x-default" href={`${process.env.NEXT_APP_WEB_HOST}/plan`} />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {jsonLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          )}
        </>
      }
    >
      {page}
    </Layout>
  );
};

export default Home;
